import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { Alert, AlertTitle, Box, Button, styled, Typography, useTheme } from "@mui/material"

import { LockOutlineRounded, LoginRounded } from "@mui/icons-material"

import { Breadcrumbs } from "../StyledComponents"
import { getAccessToken } from "../../keycloak"
import { getSupportEmail } from "../../store/environment"
import { authGuardAllows, authGuardRoles, mayModifyObservation, TANK_EDIT_ROLES } from "./allows"

// Wat de bezoeker probeerde te doen. Bepaalt de melding: die noemt de handeling zelf ("deze
// waarneming bekijken") in plaats van "dit formulier", want op een view-pagina ziet de bezoeker
// geen formulier.
const ACTIONS = ["submit", "add_tank", "view", "edit", "browse", "analyse", "manage_users", "manage_model"]

const actionKey = (prefix, action) => `${prefix}_${ACTIONS.includes(action) ? action : "view"}`

const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (character) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[character]
))

const StyledAlert = styled(Alert)(({ theme }) => ({
  alignItems: "flex-start",
  "& .MuiAlertTitle-root": { fontWeight: 600, marginBottom: theme.spacing(0.5) },
  "& a": { color: "inherit" }
}))

/**
 * Niet ingelogd is geen fout, maar de normale toestand van een bezoeker, vandaar `info` en geen
 * rood. De knop maakt de melding een uitweg: zonder hem moet de bezoeker zelf de inlogknop in de
 * header zoeken.
 */
export const NotAuthenticated = ({ action = "view" }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  return (
    <StyledAlert severity="info" icon={<LockOutlineRounded fontSize="small" />}>
      <AlertTitle>{t(actionKey("Auth_signin_title", action))}</AlertTitle>
      <Typography variant="body2">{t(actionKey("Auth_signin_body", action))}</Typography>
      <Button
        variant="contained"
        size="small"
        endIcon={<LoginRounded fontSize="small" />}
        onClick={() => dispatch(getAccessToken())}
        sx={{ mt: 1.5 }}
      >
        {t("Log_in")}
      </Button>
    </StyledAlert>
  )
}

/** Wel ingelogd, maar zonder de rol: hier loopt het echt vast, dus blijft de melding rood. */
export const NotAuthorized = ({ requiredRole }) => {
  const { t } = useTranslation()
  const accountInfo = useSelector((state) => state.accountInfo)

  const subject = `Eggspedition ${requiredRole} request`
  const body = `Hello,\n\nI would like to have the ${requiredRole} role in Eggspedition.\n\nKind regards,${accountInfo?.name ? `\n${accountInfo.name}` : ""}`
  const mailto = `mailto:${getSupportEmail()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

  // De melding bevat opmaak (<b>, <a>) en komt daarom als HTML uit de vertaling. i18next staat hier
  // op escapeValue: false, dus de rol, die uit de URL kan komen, escapen we zelf.
  const html = [
    t("Auth_role_required", { role: escapeHtml(requiredRole) }),
    t("Auth_role_request_access", { href: mailto })
  ].join(" ")

  return (
    <StyledAlert severity="error" icon={<LockOutlineRounded fontSize="small" />}>
      <AlertTitle>{t("Auth_no_access_title")}</AlertTitle>
      <Typography variant="body2" dangerouslySetInnerHTML={{ __html: html }} />
    </StyledAlert>
  )
}

const StyledShell = styled("div")(({ theme }) => ({
  width: 960, maxWidth: "100%",
  margin: "auto",
  padding: theme.spacing(6, 2), boxSizing: "border-box"
}))

/**
 * Laat `children` alleen zien aan wie is ingelogd en, als `roles` is meegegeven, één van die
 * rollen heeft. Anders komt de melding in de plaats van het formulier.
 *
 * `shell` zet de melding in een eigen paginacontainer met breadcrumbs. Nodig voor routes waarvan
 * het paginacomponent zelf niet zonder account kan renderen; een pagina die dat wel kan, plaatst
 * `NotAuthenticated` / `NotAuthorized` liever op de plek van het formulier.
 */
export const AuthGuard = ({ roles, clientRoles, action = "view", shell = false, children }) => {
  const theme = useTheme()
  const accountInfo = useSelector((state) => state.accountInfo)

  const required = authGuardRoles(roles)

  if (authGuardAllows({ accountInfo, roles, clientRoles })) return children

  const message = accountInfo
    ? <NotAuthorized requiredRole={[...required, ...(clientRoles?.roles || [])].join(" / ")} />
    : <NotAuthenticated action={action} />

  if (!shell) return message

  return (
    <StyledShell>
      <Breadcrumbs />
      <Box sx={{ mt: theme.spacing(2) }}>{message}</Box>
    </StyledShell>
  )
}

export { authGuardAllows, authGuardRoles, mayModifyObservation, TANK_EDIT_ROLES }
